import { createResource, createMemo, createSignal, Show, For, type JSX } from 'solid-js';
import { A, useParams } from '@solidjs/router';
import {
  fetchStrainBySlug,
  fetchBatchesForStrain,
  fetchStockForStrain,
  fetchPharmacies,
  formatPct,
  IRRADIATION_LABELS,
  type BatchRow,
} from '@lib/supabase/queries';
import { fetchPriceHistory } from '@lib/supabase/priceHistory';
import PriceHistoryChart from '@components/strain/PriceHistoryChart';
import MolecularHive, { type HiveTerpene } from '@components/hive/MolecularHive';
import TempDial from '@components/ui/TempDial';
import BatchSelector from '@components/ui/BatchSelector';
import StockBadge from '@components/ui/StockBadge';
import WatchToggle from '@components/strain/WatchToggle';
import PortfolioToggle from '@components/strain/PortfolioToggle';
import StarRating from '@components/strain/StarRating';
import PageHead from '@components/seo/PageHead';
import { productJsonLd } from '@lib/seo';
import { fetchStrainRatingSummary } from '@lib/supabase/ratings';
import MedicalStatusGate from '@components/medical/MedicalStatusGate';
import StrainImagesSection from '@components/strain/StrainImagesSection';
import StrainReviewsSection from '@components/strain/StrainReviewsSection';
import { recommendedWindow, ACTIVATION_TEMP_C, type TerpeneSlug } from '@medical/activation';
import { authStore } from '@stores/session';

const TERPENE_LABEL: Record<TerpeneSlug, string> = {
  myrcene: 'Myrcene',
  limonene: 'Limonene',
  linalool: 'Linalool',
  caryophyllene: 'β-Caryophyllene',
  pinene: 'α-Pinene',
  humulene: 'Humulene',
};

function batchToHive(batch: BatchRow): HiveTerpene[] {
  const entries: [TerpeneSlug, number | null][] = [
    ['myrcene', batch.myrcene_pct],
    ['limonene', batch.limonene_pct],
    ['linalool', batch.linalool_pct],
    ['caryophyllene', batch.caryophyllene_pct],
    ['pinene', batch.pinene_pct],
    ['humulene', batch.humulene_pct],
  ];
  return entries
    .filter(([, pct]) => pct != null && pct > 0)
    .map(([slug, pct]) => ({
      slug,
      label: TERPENE_LABEL[slug],
      percentage: pct as number,
      activationTempC: ACTIVATION_TEMP_C[slug],
    }));
}

function batchProfile(batch: BatchRow) {
  return {
    myrcene: batch.myrcene_pct ?? 0,
    limonene: batch.limonene_pct ?? 0,
    linalool: batch.linalool_pct ?? 0,
    caryophyllene: batch.caryophyllene_pct ?? 0,
    pinene: batch.pinene_pct ?? 0,
    humulene: batch.humulene_pct ?? 0,
  };
}

export default function StrainDetailRoute(): JSX.Element {
  const params = useParams<{ slug: string }>();
  const [strain] = createResource(() => params.slug, fetchStrainBySlug);
  const [batches] = createResource(
    () => strain()?.id,
    (id) => (id ? fetchBatchesForStrain(id) : Promise.resolve([])),
  );
  const [stock] = createResource(
    () => strain()?.id,
    (id) => (id ? fetchStockForStrain(id) : Promise.resolve([])),
  );
  const [ratingSummary] = createResource(
    () => strain()?.id,
    (id) => (id ? fetchStrainRatingSummary(id) : Promise.resolve(null)),
  );

  // Price history is medical-only (RLS + the gate below), so only fetch
  // it for approved patients — the source resolves to undefined otherwise
  // and the resource never runs.
  const [priceHistory] = createResource(
    () => (authStore.isMedicalPatient() ? strain()?.id : undefined),
    (id) => fetchPriceHistory(id),
  );

  // Pharmacy display names (tiny public-read table) for the chart caption.
  const [pharmacies] = createResource(() => fetchPharmacies());
  // Medical patients are registered to a single pharmacy. Show their
  // chosen default, falling back to IPS (the formulary we currently
  // ingest) when they haven't set one.
  const chartPharmacySlug = () => authStore.profile()?.default_pharmacy_slug ?? 'ips';
  const chartPharmacyName = () =>
    (pharmacies() ?? []).find((p) => p.slug === chartPharmacySlug())?.name ??
    chartPharmacySlug().toUpperCase();

  const [selectedBatchId, setSelectedBatchId] = createSignal<string | null>(null);

  const activeBatch = createMemo(() => {
    const bs = batches() ?? [];
    const id = selectedBatchId() ?? bs[0]?.id;
    return bs.find((b) => b.id === id) ?? bs[0] ?? null;
  });

  const window = createMemo(() => {
    const b = activeBatch();
    return b ? recommendedWindow(batchProfile(b)) : { min: 170, max: 190 };
  });

  return (
    <Show when={strain()} fallback={<p class="p-8">Loading strain…</p>}>
      {(s) => (
        <section class="px-4 sm:px-6 py-10 max-w-5xl mx-auto grid md:grid-cols-[1fr_280px] gap-8">
          <PageHead
            title={`${s().brand} ${s().name} · Terp Track`}
            description={`Terpene profile, recommended vape temperature, and live pharmacy stock for ${s().brand} ${s().name}${s().lineage ? ` (${s().lineage})` : ''}.`}
            canonical={`/strains/${s().slug}`}
            jsonLd={productJsonLd({
              name: s().name,
              brand: s().brand,
              category: s().category,
              canonical: `/strains/${s().slug}`,
              description: s().genetics,
              avgStars: ratingSummary()?.avg_stars ?? null,
              ratingCount: ratingSummary()?.rating_count ?? null,
            })}
          />
          <div>
            <p class="text-xs text-brand-green/80 uppercase tracking-wide">{s().brand}</p>
            <h1 class="font-display text-3xl sm:text-4xl font-bold">{s().name}</h1>
            <div class="flex items-center gap-2 mt-2 flex-wrap">
              <span class="text-sm text-brand-green/80">{s().genetics ?? s().category}</span>
              <Show when={s().lineage}>
                <span class="rounded-full bg-brand-hex px-2 py-0.5 text-xs font-medium capitalize">
                  {s().lineage}
                </span>
              </Show>
            </div>

            <div class="mt-4">
              <StarRating strainId={s().id} />
            </div>

            <Show when={(batches() ?? []).length > 0}>
              <div class="mt-6 flex items-center gap-4">
                <BatchSelector
                  batches={(batches() ?? []).map((b, i) => ({
                    id: b.id,
                    label: b.batch_number,
                    isCurrent: i === 0,
                  }))}
                  selectedId={activeBatch()?.id ?? ''}
                  onSelect={setSelectedBatchId}
                />
              </div>
            </Show>

            <Show
              when={activeBatch()}
              fallback={
                <div class="text-sm text-brand-green/80 mt-8">
                  <h2 class="font-display text-xl font-semibold text-brand-ink">
                    Terpene Profile
                  </h2>
                  <p class="mt-2">No verified batches yet.</p>
                  <A
                    href={`/coa/upload?strain=${encodeURIComponent(s().slug)}`}
                    class="inline-block mt-2 text-xs rounded-full bg-brand-hex text-brand-green px-3 py-1 hover:bg-brand-hex-deep transition-colors"
                  >
                    Upload a COA for this strain →
                  </A>
                </div>
              }
            >
              {(batch) => (
                <>
                  <h2 class="font-display text-xl font-semibold mt-8">Terpene Profile</h2>
                  <div class="mt-3" style={{ '--hex-size': '6.5rem' } as JSX.CSSProperties}>
                    <MolecularHive terpenes={batchToHive(batch())} wrap />
                  </div>
                </>
              )}
            </Show>

            <StrainImagesSection strainId={s().id} />
            <StrainReviewsSection strainId={s().id} strainSlug={s().slug} />

            <p class="mt-10 text-[11px] leading-relaxed text-brand-green/60 italic max-w-prose border-t border-brand-hex/60 pt-4">
              Some of the information shown here may be sourced from third parties and
              may not accurately reflect the medication it is associated with. Descriptions
              are provided for context on strain lineage and history only, which can also
              be inaccurate. Nothing on this page is medical advice &ndash; always consult your
              prescribing doctor about what is suitable for your condition.
            </p>
          </div>

          <aside class="md:sticky md:top-6 self-start flex flex-col gap-4">
            <div class="flex flex-col gap-2">
              <A
                href={`/coa/upload?strain=${encodeURIComponent(s().slug)}`}
                class="text-center cursor-pointer rounded-full bg-brand-green text-brand-cream hover:bg-brand-moss text-sm font-medium px-4 py-2 transition-colors"
              >
                Upload COA
              </A>
              <Show when={authStore.user()}>
                <A
                  href={`/forum?category=reviews&strain=${encodeURIComponent(s().slug)}`}
                  class="text-center cursor-pointer rounded-full bg-brand-green text-brand-cream hover:bg-brand-moss text-sm font-medium px-4 py-2 transition-colors"
                >
                  Submit a review
                </A>
              </Show>
              <PortfolioToggle strainId={s().id} />
              <WatchToggle strainId={s().id} />
            </div>
            <div class="rounded-xl border border-brand-hex bg-white p-4">
              <h3 class="font-display font-semibold">Recommended Vape Temperature</h3>
              <Show
                when={activeBatch()}
                fallback={
                  <p class="text-xs text-brand-green/80 mt-2">
                    Temperature data not yet available for this strain.
                  </p>
                }
              >
                <div class="flex justify-center mt-2">
                  <TempDial minTempC={window().min} maxTempC={window().max} />
                </div>
              </Show>
            </div>
            <div class="rounded-xl border border-brand-hex bg-white p-4">
              <h3 class="font-display font-semibold">Live Stock</h3>
              <MedicalStatusGate
                message={(() => {
                  const count = (stock() ?? []).length;
                  return count > 0
                    ? `In stock across ${count} pharmac${count === 1 ? 'y' : 'ies'} \u2013 confirm your medical status for more details.`
                    : 'Confirm your medical status to view live pharmacy stock.';
                })()}
                when={() => (
              <ul class="mt-3 space-y-3 text-sm">
                <For
                  each={stock() ?? []}
                  fallback={<li class="text-brand-green/60">No stock data.</li>}
                >
                  {(row) => (
                    <li class="flex flex-col gap-1 pb-2 last:pb-0 last:border-0 border-b border-brand-hex/60">
                      <div class="flex items-center justify-between">
                        <span class="capitalize font-medium">{row.pharmacy_slug}</span>
                        <StockBadge status={row.status} />
                      </div>
                      <div class="flex items-center justify-between text-xs text-brand-green/80">
                        <span>
                          {row.thc_pct_advertised != null && `THC ${formatPct(row.thc_pct_advertised, row.thc_pct_is_upper_bound)}`}
                          {row.thc_pct_advertised != null && row.cbd_pct_advertised != null && ' · '}
                          {row.cbd_pct_advertised != null && `CBD ${formatPct(row.cbd_pct_advertised, row.cbd_pct_is_upper_bound)}`}
                        </span>
                        <Show when={row.price_per_gram_gbp != null}>
                          <span class="font-medium">£{row.price_per_gram_gbp?.toFixed(2)}/g</span>
                        </Show>
                      </div>
                      <Show when={row.irradiation}>
                        <span
                          class={`self-start text-xs px-2 py-0.5 rounded-full ${
                            row.irradiation === 'none'
                              ? 'bg-emerald-100 text-emerald-900'
                              : 'bg-amber-100 text-amber-900'
                          }`}
                        >
                          {IRRADIATION_LABELS[row.irradiation!]}
                        </span>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
                )}
              />
            </div>

            <MedicalStatusGate
              message="Confirm your medical status to view this strain's price history."
              when={() => (
                <PriceHistoryChart
                  points={priceHistory() ?? []}
                  pharmacySlug={chartPharmacySlug()}
                  pharmacyName={chartPharmacyName()}
                />
              )}
            />
          </aside>
        </section>
      )}
    </Show>
  );
}
